import { useState } from "react";
import { Printer } from "lucide-react";
import { theme, card, type as text, pageHeading, pageSubtitle } from "../theme";
import { fmtDate } from "../lib/format";
import { AssuranceSnapshotView, type PackSnapshot } from "../components/AssuranceSnapshotView";

// Public (unauthenticated) page where an insurer, broker, underwriter or customer reads one
// assurance pack — /assurance-share#<token>, with the pass key typed on arrival.
//
// The mechanics are the submission share's (pages/SubmissionShare.tsx): the token is in the URL
// fragment, which is never sent to a server, so it stays out of request logs and referrer
// headers; the pass key, sent in its own message, is the other half. Both go to the API in the
// request body. No session, nothing else reachable: one frozen snapshot, as it was published.

const API = import.meta.env.VITE_API_BASE_URL as string;

interface SharedPack {
  organisation: string;
  sharedWith: string;
  expiresAt: string;
  version: number;
  snapshot: PackSnapshot;
}

export function AssuranceSharePage() {
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [passKey, setPassKey] = useState("");
  const [view, setView] = useState<SharedPack | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function open(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !passKey.trim() || busy) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/assurance-share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, passKey: passKey.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "This link is not valid.");
      }
      setView(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "This link is not valid.");
    } finally { setBusy(false); }
  }

  const page: React.CSSProperties = {
    minHeight: "100vh", background: theme.color.bg, color: theme.color.text, padding: "40px 16px",
  };
  const panel: React.CSSProperties = {
    ...card, maxWidth: 860, margin: "0 auto", padding: 24,
  };

  if (!token) {
    return (
      <div style={page}>
        <div style={panel}>
          <h1 style={{ ...pageHeading, margin: 0 }}>Security assurance packs</h1>
          <p style={{ ...pageSubtitle, marginTop: 8 }}>
            This page opens an assurance pack an organisation has shared with you: a summary of its
            security controls, certification, incident history and continuity, taken from its own
            records on the Abacas Security Compliance Platform.
          </p>
          <p style={{ ...pageSubtitle, marginTop: 8 }}>
            You need two things, sent to you separately: the link, and a pass key. Use the full link
            from the first message — copying only part of it leaves the reference behind — and enter
            the pass key from the second.
          </p>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div style={page}>
        <form style={panel} onSubmit={open}>
          <h1 style={{ ...pageHeading, margin: 0 }}>Security assurance pack</h1>
          <p style={{ ...pageSubtitle, marginTop: 8 }}>
            An organisation has shared its assurance pack with you. Enter the pass key from the second
            message it sent. The link and the pass key travel separately on purpose, so neither on its
            own opens anything.
          </p>
          <input
            value={passKey}
            onChange={(e) => setPassKey(e.target.value)}
            placeholder="ABCD-EFGH-JKMN-PQRS"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            style={{
              width: "100%", boxSizing: "border-box", marginTop: 16, padding: "10px 12px", ...text.mono, fontSize: text.sectionTitle.fontSize, letterSpacing: "1px",
              textTransform: "uppercase", background: "var(--c-panelAlt)",
              border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.heading,
            }}
          />
          {error && <div style={{ marginTop: 12, fontSize: text.hint.fontSize, color: theme.color.dangerText }}>{error}</div>}
          <button
            type="submit"
            disabled={busy || !passKey.trim()}
            style={{
              marginTop: 16, padding: "9px 18px", fontSize: text.body.fontSize, fontWeight: 600, borderRadius: 3,
              border: "none", cursor: "pointer", background: theme.color.accent, color: "#fff",
              opacity: busy || !passKey.trim() ? 0.6 : 1,
            }}
          >
            {busy ? "Opening…" : "Open assurance pack"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={page}>
      <style>{"@media print { .assurance-no-print { display: none !important; } }"}</style>
      <div style={panel}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 320px" }}>
            <h1 style={{ ...pageHeading, margin: 0 }}>
              {view.organisation} — {view.snapshot.title}
            </h1>
            <div style={{ ...pageSubtitle, marginTop: 6 }}>
              Shared with {view.sharedWith}, version {view.version}, link open until {fmtDate(view.expiresAt)}
            </div>
          </div>
          <button
            type="button"
            className="assurance-no-print"
            onClick={() => window.print()}
            style={{
              display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${theme.color.borderStrong}`,
              borderRadius: 3, padding: "6px 12px", fontSize: text.hint.fontSize, color: theme.color.muted, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            <Printer size={14} /> Print or save as PDF
          </button>
        </div>

        <div style={{ marginTop: 20, borderTop: `1px solid ${theme.color.border}`, paddingTop: 20 }}>
          <AssuranceSnapshotView snapshot={view.snapshot} />
        </div>

        <p style={{ ...text.meta, marginTop: 16, lineHeight: 1.5 }}>
          This is the pack as the organisation published it, and nothing else — it does not change
          after publication. Your access has been logged for the organisation's audit trail. Save a
          copy if you need one: the link expires and can be withdrawn.
        </p>
      </div>
    </div>
  );
}
